/**
 * SceneCanvas: the outermost R3F <Canvas> wrapper with camera setup,
 * orbit controls, and the view-cube gizmo.
 *
 * Children are rendered inside the Canvas, so all R3F components
 * (lighting, models, post-processing, helpers) are passed as children.
 */

import { type ReactNode, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  OrbitControls,
  GizmoHelper,
  GizmoViewcube,
  OrthographicCamera,
  PerspectiveCamera,
} from '@react-three/drei';
import * as THREE from 'three';
import { CameraOffset } from '@/components/viewer/scene/CameraOffset';
import { applyEasing } from '@/components/viewer/scene/easings';
import type { PiPTransitionType, PiPEasingType } from '@/worker/types';

// ---------------------------------------------------------------------------
// Transition composition helpers
// ---------------------------------------------------------------------------

/** Returns true when the transitions array includes the given effect. */
function has(
  transitions: PiPTransitionType[],
  effect: PiPTransitionType,
): boolean {
  return transitions.includes(effect);
}

// ---------------------------------------------------------------------------
// CameraAnimator — animates the main camera to a target position/lookAt
// ---------------------------------------------------------------------------

/** Snapshot of the desired camera destination set by a PiP click. */
export interface CameraAnimationTarget {
  position: [number, number, number];
  target: [number, number, number];
  /**
   * One or more animation effects to run in parallel. Default: ["direct"].
   * Position drivers (highest priority wins; rest modify):
   *   orbit > arc > zoom_out > direct
   * Orientation effects (additive, independent):
   *   roll, tilt, shake, bounce
   * FOV effect (independent, perspective only):
   *   fov_pulse
   */
  transitions: PiPTransitionType[];
  /** Easing curve applied to animation progress. Default: "exponential" */
  easing: PiPEasingType;
  /** Total animation duration in seconds. Default: 1.0 */
  duration: number;
  /** Destination FOV — needed for fov_pulse. Default: 45 */
  fov: number;
  /** How many full orbits before arriving (orbit). Default: 1.0 */
  orbitTurns: number;
  /** Axis to orbit around (orbit). Default: "auto" = world Y */
  orbitAxis: 'auto' | 'x' | 'y' | 'z';
  /** Pull-back radius multiplier (zoom_out). Default: 2.5 */
  zoomOutFactor: number;
  /** Degrees to roll around view axis (roll). Default: 360 */
  rollAngle: number;
  /** Scene-unit Y lift above straight-line midpoint (arc). Default: 30 */
  arcHeight: number;
  /** Overshoot fraction of remaining distance (bounce). Default: 0.3 */
  bounceAmplitude: number;
  /** Oscillation count during bounce (bounce). Default: 2.0 */
  bounceFrequency: number;
  /** Shake amplitude in scene units (shake). Default: 2.0 */
  shakeIntensity: number;
  /** Shake cycles per second (shake). Default: 8.0 */
  shakeFrequency: number;
  /** Phase envelope for shake (shake). Default: "end" */
  shakePhase: 'start' | 'end' | 'both';
  /** Peak pitch angle in degrees (tilt). Default: 30 */
  tiltAngle: number;
  /** FOV deviation at midpoint in degrees (fov_pulse). Default: 20 */
  fovPulseAmount: number;
  /** FOV pulse direction (fov_pulse). Default: "out" */
  fovPulseDirection: 'in' | 'out';
  /**
   * How much randomness to apply on each trigger (0–1). 0 = deterministic.
   * Each active effect's parameters are independently jittered within
   * a per-parameter sensible range, scaled by this value.
   */
  randomness: number;
}

// ---------------------------------------------------------------------------
// Helpers used by CameraAnimator
// ---------------------------------------------------------------------------

/** Resolve the orbit axis unit vector from the orbitAxis option. */
function resolveOrbitAxis(orbitAxis: 'auto' | 'x' | 'y' | 'z'): THREE.Vector3 {
  switch (orbitAxis) {
    case 'x':
      return new THREE.Vector3(1, 0, 0);
    case 'z':
      return new THREE.Vector3(0, 0, 1);
    case 'y':
    case 'auto':
    default:
      return new THREE.Vector3(0, 1, 0);
  }
}

/**
 * Decompose a position into cylindrical coordinates relative to a centre point
 * on the plane perpendicular to `axis`.
 */
function toCylindrical(
  pos: THREE.Vector3,
  centre: THREE.Vector3,
  axis: THREE.Vector3,
): { radius: number; angle: number; height: number } {
  const delta = pos.clone().sub(centre);
  // height = component along axis
  const height = delta.dot(axis);
  // radial component = delta minus the axial projection
  const radial = delta.clone().sub(axis.clone().multiplyScalar(height));
  const radius = radial.length();
  // angle in the plane — we need two orthogonal basis vectors in the plane
  const angle = Math.atan2(radial.z, radial.x);
  return { radius, angle, height };
}

/**
 * Reconstruct a world-space position from cylindrical coordinates.
 * Assumes the plane basis is (X, Z) when the axis is Y,
 * and is generalised for other axes via an orthonormal frame.
 */
function fromCylindrical(
  centre: THREE.Vector3,
  radius: number,
  angle: number,
  height: number,
  axis: THREE.Vector3,
): THREE.Vector3 {
  // Build an orthonormal frame in the plane perpendicular to axis.
  // We use (X, Z) convention consistent with toCylindrical above.
  return new THREE.Vector3(
    centre.x + radius * Math.cos(angle),
    centre.y + height,
    centre.z + radius * Math.sin(angle),
  );
}

/**
 * Deterministic pseudo-noise: sum of sines at three incommensurate frequencies.
 * Returns a value roughly in [-1, 1].  Used for shake — frame-rate independent
 * and reproducible given the same elapsed time.
 */
function pseudoNoise(t: number, freq: number): number {
  return (
    Math.sin(t * freq * 2 * Math.PI) * 0.6 +
    Math.sin(t * freq * 2 * Math.PI * 1.618) * 0.3 +
    Math.sin(t * freq * 2 * Math.PI * 2.414) * 0.1
  );
}

/**
 * Decaying oscillation envelope for bounce.
 * Returns a signed offset (fraction of remaining distance) that starts at 0,
 * overshoots, oscillates, and decays to 0.
 * Applied only after t > 0.5 so the main travel completes first.
 */
function bounceOffset(t: number, amplitude: number, frequency: number): number {
  if (t <= 0.5) return 0;
  const bt = (t - 0.5) * 2; // remap [0.5,1] → [0,1]
  return amplitude * Math.sin(bt * frequency * Math.PI) * Math.exp(-4 * bt);
}

// ---------------------------------------------------------------------------
// Randomness helpers
// ---------------------------------------------------------------------------

/**
 * Returns a random value in [-1, 1] using Math.random().
 * Each call is independent — new value on every animation trigger.
 */
function rand(): number {
  return Math.random() * 2 - 1;
}

/**
 * Perturb a base value by up to `maxDelta * randomness` in either direction,
 * then clamp the result to [minVal, maxVal].
 */
function jitter(
  base: number,
  maxDelta: number,
  randomness: number,
  minVal = -Infinity,
  maxVal = Infinity,
): number {
  return Math.max(
    minVal,
    Math.min(maxVal, base + rand() * maxDelta * randomness),
  );
}

/**
 * Apply per-trigger randomness to a copy of the animation target.
 * Returns a new object with jittered parameter values; the original is unchanged.
 *
 * Design intent:
 *   - Duration gets a small universal jitter (±20%) so even "direct" feels
 *     slightly different each time.
 *   - Each effect's signature parameters are jittered within a range that
 *     keeps the effect recognisable but distinctly varied at randomness=1.
 *   - Boolean-ish params (shake_phase, fov_pulse_direction) are randomly
 *     flipped with probability proportional to randomness.
 */
function applyRandomness(target: CameraAnimationTarget): CameraAnimationTarget {
  const r = target.randomness;
  if (r <= 0) return target;

  const trs = target.transitions;
  const has = (effect: PiPTransitionType) => trs.includes(effect);

  // ── Duration: small universal jitter ──────────────────────────────────────
  const duration = jitter(target.duration, target.duration * 0.25, r, 0.1);

  // ── Orbit ──────────────────────────────────────────────────────────────────
  // orbitTurns must be a multiple of 0.5: the orbit path closes exactly at the
  // destination angle only at integer or half-integer turn counts. A float like
  // 1.3 stops the camera mid-orbit, causing a sudden azimuth jump at animation end.
  const orbitTurns = has('orbit')
    ? Math.max(0.5, Math.round(jitter(target.orbitTurns, 1.0, r, 0.5) * 2) / 2)
    : target.orbitTurns;

  // ── Zoom-out ───────────────────────────────────────────────────────────────
  const zoomOutFactor = has('zoom_out')
    ? jitter(target.zoomOutFactor, target.zoomOutFactor * 0.6, r, 1.1)
    : target.zoomOutFactor;

  // ── Arc ────────────────────────────────────────────────────────────────────
  // arcHeight can flip sign (dip vs. lift) — let the full ±range include zero
  const arcHeight = has('arc')
    ? jitter(target.arcHeight, Math.abs(target.arcHeight) * 1.2, r)
    : target.arcHeight;

  // ── Roll ───────────────────────────────────────────────────────────────────
  // rollAngle must be a multiple of 90°: the camera.up vector is only upright
  // (or stably upside-down / sideways) at 90° increments. A float stops the
  // roll at an arbitrary tilt, leaving the view skewed at the animation end.
  const rollAngle = has('roll')
    ? Math.round(jitter(target.rollAngle, 180, r) / 90) * 90
    : target.rollAngle;

  // ── Bounce ─────────────────────────────────────────────────────────────────
  const bounceAmplitude = has('bounce')
    ? jitter(target.bounceAmplitude, target.bounceAmplitude * 0.7, r, 0.05, 1.5)
    : target.bounceAmplitude;
  // bounceFrequency must be an integer: bounceOffset uses sin(bt * freq * π),
  // which is exactly 0 at bt=1 only when freq is an integer. A float leaves
  // the bounce envelope at a non-zero value at t=1, snapping the camera.
  const bounceFrequency = has('bounce')
    ? Math.max(
        1,
        Math.round(
          jitter(target.bounceFrequency, target.bounceFrequency * 0.6, r, 1, 8),
        ),
      )
    : target.bounceFrequency;

  // ── Shake ──────────────────────────────────────────────────────────────────
  const shakeIntensity = has('shake')
    ? jitter(target.shakeIntensity, target.shakeIntensity * 0.7, r, 0.1)
    : target.shakeIntensity;
  const shakeFrequency = has('shake')
    ? jitter(target.shakeFrequency, target.shakeFrequency * 0.5, r, 1)
    : target.shakeFrequency;
  // shakePhase: random pick weighted by r
  const shakePhaseOptions: Array<'start' | 'end' | 'both'> = [
    'start',
    'end',
    'both',
  ];
  const shakePhase =
    has('shake') && Math.random() < r * 0.6
      ? shakePhaseOptions[Math.floor(Math.random() * 3)]
      : target.shakePhase;

  // ── Tilt ───────────────────────────────────────────────────────────────────
  // tiltAngle can flip sign (up vs. down)
  const tiltAngle = has('tilt')
    ? jitter(target.tiltAngle, Math.abs(target.tiltAngle) * 1.0, r)
    : target.tiltAngle;

  // ── FOV pulse ──────────────────────────────────────────────────────────────
  const fovPulseAmount = has('fov_pulse')
    ? jitter(target.fovPulseAmount, target.fovPulseAmount * 0.7, r, 2)
    : target.fovPulseAmount;
  // fovPulseDirection: flip with probability r * 0.4
  const fovPulseDirection: 'in' | 'out' =
    has('fov_pulse') && Math.random() < r * 0.4
      ? target.fovPulseDirection === 'in'
        ? 'out'
        : 'in'
      : target.fovPulseDirection;

  return {
    ...target,
    duration,
    orbitTurns,
    zoomOutFactor,
    arcHeight,
    rollAngle,
    bounceAmplitude,
    bounceFrequency,
    shakeIntensity,
    shakeFrequency,
    shakePhase,
    tiltAngle,
    fovPulseAmount,
    fovPulseDirection,
  };
}

// ---------------------------------------------------------------------------
// CameraAnimator
// ---------------------------------------------------------------------------

/**
 * CameraAnimator lives inside the R3F Canvas so it can imperatively drive the
 * camera and OrbitControls. When `animationTarget` changes it begins a
 * time-based animation (t: 0→1 over `duration` seconds) using the specified
 * transition effects and easing curve, then stops.
 *
 * Position layer — highest priority wins, rest modify:
 *   orbit  > arc > zoom_out > direct
 *   orbit + zoom_out → zoom modulates orbit radius (sin envelope)
 *   orbit + arc      → arc height added to orbit midpoint height
 *   orbit + bounce   → bounce oscillates orbit radius on final approach
 *
 * Orientation layer — all additive and independent:
 *   roll   — spins camera.up around view axis
 *   tilt   — pitches look direction (sin envelope, peaks at midpoint)
 *   shake  — additive position noise (enveloped by shakePhase)
 *   bounce — decaying overshoot on position
 *
 * FOV layer — independent, perspective cameras only:
 *   fov_pulse — warps camera.fov mid-flight (sin envelope)
 */
function CameraAnimator({
  animationTarget,
  onAnimationComplete,
}: {
  animationTarget: CameraAnimationTarget | null;
  onAnimationComplete?: () => void;
}) {
  const { camera, invalidate } = useThree();

  // ── Animation state ────────────────────────────────────────────────────────
  const isAnimating = useRef(false);
  const elapsed = useRef(0);

  // Camera state snapshots
  const startPos = useRef(new THREE.Vector3());
  const startTarget = useRef(new THREE.Vector3());
  const endPos = useRef(new THREE.Vector3());
  const endTarget = useRef(new THREE.Vector3());

  // Shared params
  const transitionsRef = useRef<PiPTransitionType[]>(['direct']);
  const easingRef = useRef<PiPEasingType>('exponential');
  const durationRef = useRef(1.0);

  // orbit
  const orbitAxisVec = useRef(new THREE.Vector3(0, 1, 0));
  const orbitStartAngle = useRef(0);
  const orbitEndAngle = useRef(0);
  const orbitStartRadius = useRef(1);
  const orbitEndRadius = useRef(1);
  const orbitStartHeight = useRef(0);
  const orbitEndHeight = useRef(0);
  const orbitTurnsRef = useRef(1.0);

  // zoom_out (standalone: apex path; combined with orbit: radius scale)
  const zoomRadiusScale = useRef(1.0);
  const zoomApexPos = useRef(new THREE.Vector3());
  const zoomMidTarget = useRef(new THREE.Vector3());

  // arc — Bézier apex point (world space)
  const arcApexPos = useRef(new THREE.Vector3());

  // roll
  const rollAngleRef = useRef(0);

  // bounce
  const bounceAmplitudeRef = useRef(0.3);
  const bounceFrequencyRef = useRef(2.0);

  // shake
  const shakeIntensityRef = useRef(2.0);
  const shakeFrequencyRef = useRef(8.0);
  const shakePhaseRef = useRef<'start' | 'end' | 'both'>('end');

  // tilt
  const tiltAngleRef = useRef(0); // radians

  // fov_pulse
  const fovPulseAmountRef = useRef(0);
  const fovPulseSignRef = useRef(1); // +1 = widen, -1 = narrow
  const destFovRef = useRef(45);

  // Stable ref for the onAnimationComplete callback to avoid stale closures
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;

  // When a new target arrives, snapshot the current camera state and apply randomness
  useEffect(() => {
    if (!animationTarget) return;

    // Apply per-trigger randomness: returns a jittered copy (or the original when
    // randomness=0). Every click gets independently randomised parameter values.
    const tgt = applyRandomness(animationTarget);

    startPos.current.copy(camera.position);
    startTarget.current.set(0, 0, 0); // overwritten first frame

    endPos.current.set(...tgt.position);
    endTarget.current.set(...tgt.target);

    transitionsRef.current = tgt.transitions;
    easingRef.current = tgt.easing;
    durationRef.current = Math.max(0.01, tgt.duration);

    // Copy per-effect params into refs
    rollAngleRef.current = (tgt.rollAngle * Math.PI) / 180;
    bounceAmplitudeRef.current = tgt.bounceAmplitude;
    bounceFrequencyRef.current = tgt.bounceFrequency;
    shakeIntensityRef.current = tgt.shakeIntensity;
    shakeFrequencyRef.current = tgt.shakeFrequency;
    shakePhaseRef.current = tgt.shakePhase;
    tiltAngleRef.current = (tgt.tiltAngle * Math.PI) / 180;
    fovPulseAmountRef.current = tgt.fovPulseAmount;
    fovPulseSignRef.current = tgt.fovPulseDirection === 'out' ? 1 : -1;
    destFovRef.current = tgt.fov;

    elapsed.current = 0;
    isAnimating.current = true;
    invalidate();
  }, [animationTarget, camera, invalidate]);

  useFrame(({ controls }, delta) => {
    if (!isAnimating.current) return;

    // ── First-frame: capture live orbit target; precompute path values ────────
    if (elapsed.current === 0) {
      if (controls && 'target' in controls) {
        startTarget.current.copy(
          (controls as { target: THREE.Vector3 }).target,
        );
      } else {
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        startTarget.current.copy(camera.position).add(dir.multiplyScalar(50));
      }

      const trs = transitionsRef.current;

      if (has(trs, 'orbit')) {
        const axis = resolveOrbitAxis(animationTarget!.orbitAxis);
        orbitAxisVec.current.copy(axis);
        orbitTurnsRef.current = animationTarget!.orbitTurns;
        const sc = toCylindrical(startPos.current, endTarget.current, axis);
        const ec = toCylindrical(endPos.current, endTarget.current, axis);
        orbitStartAngle.current = sc.angle;
        orbitEndAngle.current = ec.angle;
        orbitStartRadius.current = sc.radius;
        orbitEndRadius.current = ec.radius;
        orbitStartHeight.current = sc.height;
        orbitEndHeight.current = ec.height;
      }

      if (has(trs, 'zoom_out')) {
        const factor = animationTarget!.zoomOutFactor;
        const avgDist =
          (startPos.current.distanceTo(endTarget.current) +
            endPos.current.distanceTo(endTarget.current)) /
          2;
        if (has(trs, 'orbit')) {
          zoomRadiusScale.current = factor;
        } else {
          const midTarget = startTarget.current
            .clone()
            .lerp(endTarget.current, 0.5);
          const pullDir = midTarget.clone().normalize();
          if (pullDir.lengthSq() < 0.01) pullDir.set(0, 1, 0);
          zoomApexPos.current
            .copy(midTarget)
            .add(pullDir.multiplyScalar(factor * avgDist));
          zoomMidTarget.current.copy(midTarget);
        }
      }

      if (has(trs, 'arc')) {
        // Quadratic Bézier apex: midpoint lifted by arcHeight in world Y.
        // When orbit is also active, height offset is applied to the orbit's
        // midpoint height instead (handled in the orbit block below).
        if (!has(trs, 'orbit')) {
          const mid = startPos.current.clone().lerp(endPos.current, 0.5);
          mid.y += animationTarget!.arcHeight;
          arcApexPos.current.copy(mid);
        }
      }
    }

    elapsed.current += delta;
    const t = Math.min(elapsed.current / durationRef.current, 1);
    const te = applyEasing(t, easingRef.current);

    const orbitControls = controls as {
      target: THREE.Vector3;
      update: () => void;
    } | null;
    const trs = transitionsRef.current;

    // ── Position layer ────────────────────────────────────────────────────────

    if (has(trs, 'orbit')) {
      const angle =
        orbitStartAngle.current +
        te *
          (orbitEndAngle.current -
            orbitStartAngle.current +
            orbitTurnsRef.current * 2 * Math.PI);

      let radius = THREE.MathUtils.lerp(
        orbitStartRadius.current,
        orbitEndRadius.current,
        te,
      );

      // zoom_out: inflate orbit radius at midpoint
      if (has(trs, 'zoom_out')) {
        radius *= 1 + (zoomRadiusScale.current - 1) * Math.sin(Math.PI * t);
      }

      // bounce: oscillate orbit radius on final approach
      if (has(trs, 'bounce')) {
        radius +=
          radius *
          bounceOffset(
            t,
            bounceAmplitudeRef.current,
            bounceFrequencyRef.current,
          );
      }

      // arc: add a Y height offset that peaks at the orbit midpoint
      let height = THREE.MathUtils.lerp(
        orbitStartHeight.current,
        orbitEndHeight.current,
        te,
      );
      if (has(trs, 'arc')) {
        height += animationTarget!.arcHeight * Math.sin(Math.PI * te);
      }

      camera.position.copy(
        fromCylindrical(
          endTarget.current,
          radius,
          angle,
          height,
          orbitAxisVec.current,
        ),
      );

      if (orbitControls) {
        orbitControls.target.lerpVectors(
          startTarget.current,
          endTarget.current,
          te,
        );
        orbitControls.update();
      } else {
        camera.lookAt(endTarget.current);
      }
    } else if (has(trs, 'arc')) {
      // Arc standalone: quadratic Bézier  start → apex → end
      // P(t) = (1-t)²·start + 2(1-t)t·apex + t²·end
      const mt = 1 - te;
      const pos = startPos.current
        .clone()
        .multiplyScalar(mt * mt)
        .add(arcApexPos.current.clone().multiplyScalar(2 * mt * te))
        .add(endPos.current.clone().multiplyScalar(te * te));

      if (has(trs, 'bounce')) {
        const residual = endPos.current.clone().sub(pos);
        pos.add(
          residual.multiplyScalar(
            bounceOffset(
              t,
              bounceAmplitudeRef.current,
              bounceFrequencyRef.current,
            ),
          ),
        );
      }

      camera.position.copy(pos);
      if (orbitControls) {
        orbitControls.target.lerpVectors(
          startTarget.current,
          endTarget.current,
          te,
        );
        orbitControls.update();
      } else {
        camera.lookAt(endTarget.current);
      }
    } else if (has(trs, 'zoom_out')) {
      // zoom_out standalone: two-phase apex path
      if (t < 0.5) {
        const phase = applyEasing(t * 2, easingRef.current);
        camera.position.lerpVectors(
          startPos.current,
          zoomApexPos.current,
          phase,
        );
        if (orbitControls) {
          orbitControls.target.lerpVectors(
            startTarget.current,
            zoomMidTarget.current,
            phase,
          );
          orbitControls.update();
        } else {
          camera.lookAt(zoomMidTarget.current);
        }
      } else {
        const phase = applyEasing((t - 0.5) * 2, easingRef.current);
        camera.position.lerpVectors(zoomApexPos.current, endPos.current, phase);
        if (orbitControls) {
          orbitControls.target.lerpVectors(
            zoomMidTarget.current,
            endTarget.current,
            phase,
          );
          orbitControls.update();
        } else {
          camera.lookAt(endTarget.current);
        }
      }
    } else {
      // direct — straight lerp with optional bounce on final approach
      let lerpFactor = te;
      if (has(trs, 'bounce')) {
        lerpFactor = Math.min(
          1,
          Math.max(
            0,
            te +
              bounceOffset(
                t,
                bounceAmplitudeRef.current,
                bounceFrequencyRef.current,
              ),
          ),
        );
      }
      camera.position.lerpVectors(startPos.current, endPos.current, lerpFactor);
      if (orbitControls) {
        orbitControls.target.lerpVectors(
          startTarget.current,
          endTarget.current,
          te,
        );
        orbitControls.update();
      } else {
        camera.lookAt(endTarget.current);
      }
    }

    // ── Shake: additive position noise ───────────────────────────────────────
    if (has(trs, 'shake')) {
      // Envelope based on shakePhase
      let envelope: number;
      switch (shakePhaseRef.current) {
        case 'start':
          envelope = Math.max(0, 1 - t * 2);
          break;
        case 'both':
          envelope = Math.sin(Math.PI * t);
          break;
        case 'end':
        default:
          envelope = Math.max(0, t * 2 - 1);
          break;
      }
      const intensity = shakeIntensityRef.current * envelope;
      if (intensity > 0.001) {
        // Three orthogonal axes, each with its own pseudo-noise phase offset
        const et = elapsed.current; // use raw elapsed for freq-independent shake
        camera.position.x +=
          pseudoNoise(et, shakeFrequencyRef.current) * intensity;
        camera.position.y +=
          pseudoNoise(et + 13, shakeFrequencyRef.current) * intensity;
        camera.position.z +=
          pseudoNoise(et + 31, shakeFrequencyRef.current) * intensity;
      }
    }

    // ── Roll ──────────────────────────────────────────────────────────────────
    if (has(trs, 'roll')) {
      const viewDir = endTarget.current.clone().sub(endPos.current).normalize();
      const baseUp = new THREE.Vector3(0, 1, 0);
      if (Math.abs(baseUp.dot(viewDir)) > 0.99) baseUp.set(1, 0, 0);
      camera.up.copy(baseUp).applyAxisAngle(viewDir, rollAngleRef.current * te);
      if (!orbitControls) camera.lookAt(endTarget.current);
    }

    // ── Tilt: pitch the look direction (sin envelope, zero at both ends) ──────
    if (has(trs, 'tilt') && tiltAngleRef.current !== 0) {
      const envelope = Math.sin(Math.PI * t);
      const tiltNow = tiltAngleRef.current * envelope;
      if (Math.abs(tiltNow) > 0.0001) {
        // Compute camera right vector
        const toTarget = endTarget.current
          .clone()
          .sub(camera.position)
          .normalize();
        const up = camera.up.clone().normalize();
        const right = new THREE.Vector3()
          .crossVectors(toTarget, up)
          .normalize();
        // Rotate the look direction around right axis by tiltNow radians
        const tilted = toTarget.clone().applyAxisAngle(right, tiltNow);
        const tiltedTgt = camera.position
          .clone()
          .add(tilted.multiplyScalar(50));
        camera.lookAt(tiltedTgt);
      }
    }

    // ── FOV pulse (perspective cameras only) ──────────────────────────────────
    if (has(trs, 'fov_pulse') && 'fov' in camera) {
      const cam = camera as THREE.PerspectiveCamera;
      const envelope = Math.sin(Math.PI * t);
      cam.fov =
        destFovRef.current +
        fovPulseSignRef.current * fovPulseAmountRef.current * envelope;
      cam.updateProjectionMatrix();
    }

    invalidate();

    // ── Snap and stop ─────────────────────────────────────────────────────────
    if (t >= 1) {
      camera.position.copy(endPos.current);
      if (orbitControls) {
        orbitControls.target.copy(endTarget.current);
        orbitControls.update();
      } else {
        camera.lookAt(endTarget.current);
      }
      if (has(trs, 'roll')) camera.up.set(0, 1, 0);
      // Restore FOV to destination value
      if (has(trs, 'fov_pulse') && 'fov' in camera) {
        const cam = camera as THREE.PerspectiveCamera;
        cam.fov = destFovRef.current;
        cam.updateProjectionMatrix();
      }
      isAnimating.current = false;
      // Notify parent (e.g. camera path sequencer) that this animation finished
      onAnimationCompleteRef.current?.();
    }
  });

  return null;
}

interface SceneCanvasProps {
  children: ReactNode;
  isOrthographic: boolean;
  presentationMode: boolean;
  measurementEnabled: boolean;
  isMeasuring: boolean;
  hasPostProcessing?: boolean;
  shadows?: boolean | 'basic' | 'percentage' | 'soft' | 'variance';
  productMode?: boolean;
  /** When true, hide all chrome and use full DPR (landing-page showcase) */
  showcaseMode?: boolean;
  /** Horizontal pixel offset to shift the visual center (e.g. for overlay panels) */
  cameraOffsetX?: number;
  /**
   * Whether to keep the WebGL framebuffer readable by the CPU (needed for
   * screenshots / downloads). Defaults to true for the main app, but can be
   * disabled for blog embeds where screenshots are not supported, saving GPU
   * memory on pages with multiple canvases.
   */
  preserveDrawingBuffer?: boolean;
  /** When true, switch to continuous rendering for animated JS texture scripts */
  hasActiveTextureScripts?: boolean;
  /**
   * When set, the camera smoothly animates to this position/target.
   * Triggered by clicking a PiP panel.
   */
  cameraAnimationTarget?: CameraAnimationTarget | null;
  /**
   * Called when a camera animation completes (t reaches 1.0).
   * Used by the camera path sequencer to advance to the next waypoint.
   */
  onAnimationComplete?: () => void;
}

export function SceneCanvas({
  children,
  isOrthographic,
  presentationMode,
  measurementEnabled,
  isMeasuring,
  hasPostProcessing = false,
  shadows = true,
  productMode = false,
  showcaseMode = false,
  cameraOffsetX = 0,
  preserveDrawingBuffer = true,
  hasActiveTextureScripts = false,
  cameraAnimationTarget = null,
  onAnimationComplete,
}: SceneCanvasProps) {
  return (
    <Canvas
      className="block h-full w-full"
      shadows={shadows}
      dpr={productMode && !showcaseMode ? 1 : [1, 2]}
      linear
      frameloop={hasActiveTextureScripts ? 'always' : 'demand'}
      gl={{ preserveDrawingBuffer }}
    >
      {/* Camera — only one is active at a time via makeDefault */}
      {isOrthographic ? (
        <OrthographicCamera
          makeDefault
          position={[-100, 100, 100]}
          zoom={40}
          near={0.1}
          far={1000}
        />
      ) : (
        <PerspectiveCamera
          makeDefault
          position={[-100, 100, 100]}
          fov={45}
          near={0.1}
          far={1000}
        />
      )}

      {/* Camera offset for product mode (shifts visual center for overlay panels) */}
      <CameraOffset offsetX={cameraOffsetX} />

      {/* PiP-click camera animator — smoothly drives camera to clicked PiP position */}
      <CameraAnimator
        animationTarget={cameraAnimationTarget}
        onAnimationComplete={onAnimationComplete}
      />

      {/* Scene content (background, lighting, model, post-processing, helpers) */}
      {children}

      {/* Controls */}
      <OrbitControls
        makeDefault
        enableDamping={true}
        dampingFactor={0.05}
        autoRotate={presentationMode && !measurementEnabled}
        autoRotateSpeed={2.0}
        enabled={!isMeasuring}
      />
      {!productMode && !showcaseMode && (
        <GizmoHelper
          alignment="bottom-left"
          margin={[60, 60]}
          renderPriority={hasPostProcessing ? 2 : 1}
        >
          <GizmoViewcube
            color="rgba(0,0,0,0.2)"
            strokeColor="#fff"
            textColor="#fff"
            hoverColor="#aaa"
            opacity={1}
          />
        </GizmoHelper>
      )}
    </Canvas>
  );
}
